/*
    Hook que actúa como una capa de abstracción sobre React Router, simplificando la navegación, el manejo
    de query parameters, las rutas relativas y la detección de seguridad.
    
    Puntos clave:
        Navegación simplificada: Envuelve los métodos de React Router con utilidades directas como goTo, 
        redirectTo, goBack, goForward y refreshPage (navigate(0)).

        Gestión de Query Params: Facilita leer (getQueryParam), eliminar (removeQueryParam) o navegar 
        agregando parámetros URL en formato objeto (goToWithQueries).

        Navegación relativa limpia: Permite moverse sin hardcodear URLs mediante goToChild 
        (avanzar a un subnivel), goToSibling (cambiar entre elementos del mismo nivel) y goToParent 
        (subir de nivel).

        Detección de rutas privadas (isPrivateRoute): Valida la URL actual contra la lista global de módulos 
        públicos (APP_MODULES_GET_PUBLIC_ROUTES) usando matchPath para determinar si requiere autenticación.

*/
import { APP_MODULES_GET_PUBLIC_ROUTES } from "../../../../Web/Views/AppModules";
import { useNavigate, useLocation, useParams, matchPath } from "react-router-dom";


export const useReactNavigation = () => {

    const PARAMS = useParams();
    const NAVIGATE = useNavigate();
    const LOCATION = useLocation();

    // Obtención de ruta actual y query params
    const currentPath = LOCATION.pathname;
    const queryParams = new URLSearchParams(LOCATION.search);

    // Ruta padre de la actual (Ej: /blog/mi-post -> /blog)
    const parentPath = currentPath.replace(/\/+$/, "").split("/").slice(0, -1).join("/") || "/";

    /*
        Navegación a una ruta específica. 
            - path= Ruta de destino (Ej: "/dashboard")
            - state= Estado intermedio que qeuremos pasar a la vista de destino (Ej: email=gaston@hotmail.com)
            - replace= Reemplazar el historial actual en lugar de empujar uno nuevo.
    */
    const goTo = (path, state = null, replace = false) => {
        NAVIGATE(path, { replace, state });
    };

    // Reemplaza la ruta actual en el historial (útil tras logins o formularios)
    const redirectTo = (path, state = null) => {
        goTo(path, state, true);
    };

    // Navegación hacia atrás en el historial. steps= Número de páginas a retroceder (debe ser número negativo)
    const goBack = (steps = -1) => {
        const delta = steps > 0 ? -steps : steps;
        NAVIGATE(delta);
    };

    // Navega hacia adelante en el historial
    const goForward = () => {
        NAVIGATE(1);
    };

    // Recarga la página actual conservando el estado de React Router.
    const refreshPage = () => {
        NAVIGATE(0);
    };

    /* Navegación a una ruta añadiendo o modificando Query Params (parámetros de búsqueda)
        paramsObj= Objeto con los nuevos query params (Ej: {search: "react", page: 2})
        Ej: goToWithQueries('/courses', { category: categoryId });
    */
    const goToWithQueries = (path, paramsObj) => {
        const newQueries = new URLSearchParams(paramsObj).toString();
        NAVIGATE(`${path}?${newQueries}`);
    };

    /*
        Obtención de valor de una Query Param específico de la URL actual. 
        - key= Nombre del parámetro (Ej: "page")
    */
    const getQueryParam = (key) => {
        return queryParams.get(key);
    };

    /*
        Remueve una Query param de la URL actual sin recargar la página. 
        - Key= Nombre del parámetro a eliminar.
    */
    const removeQueryParam = (key) => {
        queryParams.delete(key);
        const searchString = queryParams.toString();
        NAVIGATE(`${currentPath}${searchString ? "?" + searchString : ""}`, { replace: true });
    };

    /*
        Navega a una sub-ruta relativa a la ruta actual, sin hardcodear el módulo.
        - segment= Segmento hijo a concatenar (Ej: "mi-post")
        Ej: estando en /blog -> goToChild(post.slug) -> /blog/mi-post
    */
    const goToChild = (segment, state = null, replace = false) => {
        const BASE = currentPath.replace(/\/+$/, "");
        const CHILD = String(segment).replace(/^\/+/, "");
        goTo(`${BASE}/${CHILD}`, state, replace);
    };

    /*
        Navega a un "hermano" dentro del mismo padre (útil en vistas de detalle).
        - segment= Segmento hermano a concatenar (Ej: "otro-post")
        Ej: estando en /blog/post-a -> goToSibling("post-b") -> /blog/post-b
    */
    const goToSibling = (segment, state = null, replace = false) => {
        const CHILD = String(segment).replace(/^\/+/, "");
        goTo(`${parentPath === "/" ? "" : parentPath}/${CHILD}`, state, replace);
    };

    // Navega a la ruta padre de la actual (Ej: estando en /blog/mi-post -> /blog)
    const goToParent = (state = null, replace = false) => {
        goTo(parentPath, state, replace);
    };


    /*
        Determina si la ruta actual es Privada comprobando contra 
        el registro dinámico de módulos públicos.
    */
    const isPrivateRoute = () => {
        const publicRoutes = APP_MODULES_GET_PUBLIC_ROUTES();
        const isPublic = publicRoutes.some((route) => {
            if (!route || !route.path) return false;
            return matchPath({ path: route.path, end: true }, currentPath) !== null;
        });
        return !isPublic;
    };

    return {
        goTo, redirectTo, goBack, goForward, refreshPage, goToWithQueries,
        getQueryParam, removeQueryParam,
        goToChild, goToSibling, goToParent, parentPath,
        currentPath, urlParams: PARAMS, // Params dinámicos (EJ: /user/:id -> urlParams.id)
        rawLocation: LOCATION, // Por si necesitamos acceso total al objeto location nativo
        isPrivateRoute,
    };
};